import { useState } from "react";
import { Avatar } from "@mui/material";
import Container from "../components/basicComponents/container";
import TextInput from "../components/basicComponents/text-input";
import TextArea from "../components/basicComponents/text-area";
import Select from "../components/basicComponents/select";
import Button from "../components/basicComponents/button";
import * as Icons from "../assets/SVGs/index";
import ImageUpload from "../components/basicComponents/image-upload";
import ToggleSwitch from "../components/basicComponents/switch-box";
import Grid from "../components/grid";
import { useDispatch, useSelector } from "react-redux";
import { selectGlobalState } from "../state-management/selectors";
import {
  addDepartment,
  editDepartment,
  removeDepartment,
} from "../state-management/actions/department-actions";
import ColorPicker from "../components/basicComponents/color-picker";
import {
  setAlert,
  setAlertSeverity,
  setAlertContext,
} from "../state-management/actions/alerts-actions";
import {
  setRowStatus,
  setDepartmentsData,
} from "../state-management/actions/global-state-actions";
import { RemovePermission } from "../utils/remove-permission";
import { convertBase64ToSvg } from "../utils/convertBase64ToSvg";
const initialNewDepartmentData = {
  name: "",
  description: "",
  icon: "",
  color_primary: "#ffffff",
  color_secondary: "#ffffff",
  manager_id: "",
  is_active: false,
};

export default function Departments() {
  const dispatch = useDispatch();
  const {
    usersData,
    departmentsData,
    articlesData,
    groupingData,
    projectsData,
    newsData,
  } = useSelector(selectGlobalState);
  const { theme: themeMode } = useSelector((state: any) => state.globalState);
  const usersOptions = usersData.map((user: any) => {
    return {
      label: `${user.firstname} ${user.lastname}`,
      value: user._id,
    };
  });
  const [editFlag, setEditFlag] = useState({
    id: "",
    flag: false,
  });
  const [newDepartment, setNewDepartment] = useState(initialNewDepartmentData);

  const handleSubmit = async () => {
    try {
      if (editFlag.flag && newDepartment) {
        const editData = {
          department_id: editFlag.id,
          ...newDepartment,
        };
        await editDepartment(editFlag.id, editData)(dispatch);
        dispatch(setAlert(true));
        dispatch(setAlertSeverity("success"));
        dispatch(setAlertContext("ویرایش با موفقیت صورت پذیرفت"));
        dispatch(
          setDepartmentsData(
            departmentsData.map((dep: any) =>
              dep._id === editFlag.id ? { ...editData, _id: editFlag.id } : dep
            )
          )
        );
        setNewDepartment(initialNewDepartmentData);
        setEditFlag({
          ...editFlag,
          flag: false,
        });
      } else {
        const response = await addDepartment(newDepartment)(dispatch);
        dispatch(
          setDepartmentsData([
            ...departmentsData,
            { ...newDepartment, _id: response.data.NewItem._id },
          ])
        );
        dispatch(setAlert(true));
        dispatch(setAlertSeverity("success"));
        dispatch(setAlertContext("ثبت با موفقیت صورت پذیرفت"));
        setNewDepartment(initialNewDepartmentData);
      }
      dispatch(setRowStatus("clear id", false));
    } catch (error) {
      console.log(error);
    }
  };

  const handleSetEditData = async (clear: boolean, id: any) => {
    const setData = departmentsData.find((item: any) => item._id === id);
    const { _id, ...cleanData } = setData;
    setEditFlag({ id: _id, flag: true });

    if (!clear && setData) {
      setNewDepartment(cleanData);
    }

    if (clear) {
      setEditFlag({ id: "", flag: false });
      setNewDepartment(initialNewDepartmentData);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const depExist = RemovePermission(id, [
        articlesData,
        groupingData,
        projectsData,
        newsData,
      ]);
      if (!depExist) {
        await removeDepartment(id)(dispatch);
        dispatch(
          setDepartmentsData(
            departmentsData.filter((dep: any) => dep._id !== id)
          )
        );
        dispatch(setAlert(true));
        dispatch(setAlertSeverity("success"));
        dispatch(setAlertContext("حذف با موفقیت صورت پذیرفت"));
      } else {
        dispatch(setAlert(true));
        dispatch(setAlertSeverity("warning"));
        dispatch(setAlertContext("دپارتمان مربوطه دارای وابستگی است"));
      }
    } catch (error) {
      console.log(error);
    }
  };

  const columns: any = [
    {
      headerName: "نماد",
      field: "icon",
      width: 100,
      cellRenderer: (params: any) => {
        if (!params.value) return "";
        return (
          <Avatar
            src={convertBase64ToSvg(
              params.value,
              themeMode === "light" ? "black" : "white"
            )}
            alt="icon"
            className="w-10 h-10"
          />
        );
      },
      pinned: "right",
    },
    { headerName: "دپارتمان", field: "name" },
    {
      headerName: "مدیر گروه",
      valueGetter: (params: any) =>
        usersOptions.find((item: any) => item.value === params.data.manager_id)
          ?.label || "",
    },
    {
      headerName: "وضعیت نمایش",
      field: "is_active",
    },
    {
      headerName: "رنگ اولیه",
      field: "color_primary",
      cellRenderer: (params: any) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            paddingRight: "20px",
            paddingLeft: "20px",
            height: "32px",
            backgroundColor: params.value || "transparent",
            color: params.value !== "#FFFFFF" ? "#ffffff" : "#000000",
          }}
        >
          {params.value}
        </div>
      ),
    },
    {
      headerName: "رنگ ثانویه",
      field: "color_secondary",
      cellRenderer: (params: any) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            paddingRight: "20px",
            paddingLeft: "20px",
            height: "32px",
            backgroundColor: params.value || "transparent",
            color: params.value !== "#ffffff" ? "#ffffff" : "#000000",
          }}
        >
          {params.value}
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col items-center w-full">
      <Container header="دپارتمان ها">
        <form className="w-full">
          <div className="flex justify-between flex-wrap gap-2">
            <div className="mb-5 mt-5 md:w-[25%] w-full">
              <TextInput
                name="departmentName"
                label="عنوان دپارتمان"
                placeholder="عنوان دپارتمان"
                RightIcon={<Icons.TitleIcon />}
                textAlign="right"
                value={newDepartment.name}
                onChange={(e) => {
                  setNewDepartment({
                    ...newDepartment,
                    name: e.target.value,
                  });
                }}
              />
            </div>
            <div className="mb-5 mt-5 md:w-[25%] w-full">
              <Select
                name="departmentManager"
                options={usersOptions}
                placeholder="مدیر دپارتمان را انتخاب کنید"
                value={newDepartment.manager_id}
                onChange={(e) => {
                  setNewDepartment({
                    ...newDepartment,
                    manager_id: e.target.value,
                  });
                }}
              />
            </div>
            <div className="mb-5 mt-5 md:w-[23%] w-full">
              <ColorPicker
                color={newDepartment.color_primary}
                setColor={(color: string) => {
                  setNewDepartment({
                    ...newDepartment,
                    color_primary: color,
                  });
                }}
                label="انتخاب رنگ اولیه"
              />
            </div>
            <div className="mb-5 mt-5 md:w-[23%] w-full">
              <ColorPicker
                color={newDepartment.color_secondary}
                setColor={(color: string) => {
                  setNewDepartment({
                    ...newDepartment,
                    color_secondary: color,
                  });
                }}
                label="انتخاب رنگ ثانویه"
              />
            </div>
          </div>
          <div className="flex flex-wrap">
            <div className="md:w-[50%] w-full">
              <TextArea
                name="description"
                placeholder="توضیحات"
                value={newDepartment.description}
                onChange={(e) => {
                  setNewDepartment({
                    ...newDepartment,
                    description: e.target.value,
                  });
                }}
                rows={10}
              />
            </div>
            <div className="md:w-[39%] w-full mr-3">
              <ImageUpload
                onImageChange={(file) => {
                  if (file) {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      const base64String = reader.result as string;
                      setNewDepartment((prev) => ({
                        ...prev,
                        icon: base64String,
                      }));
                      localStorage.setItem("depImageBase64", base64String);
                    };
                    reader.readAsDataURL(file);
                  } else {
                    setNewDepartment((prev) => ({
                      ...prev,
                      icon: "",
                    }));
                    localStorage.removeItem("depImageBase64");
                  }
                }}
                storageKey="depImageBase64"
                label="آپلود آیکون"
                defaultImage={newDepartment.icon}
              />
            </div>
          </div>
          <div className="w-full mb-5">
            <ToggleSwitch
              label=" نمایش در وبسایت"
              checked={newDepartment.is_active}
              onChange={() => {
                setNewDepartment({
                  ...newDepartment,
                  is_active: !newDepartment.is_active,
                });
              }}
            />
          </div>
          <div className="flex justify-end">
            <Button
              title={editFlag.flag ? "ویرایش" : "ثبت"}
              variant={editFlag.flag ? "edit" : "success"}
              onClick={handleSubmit}
            />
          </div>
        </form>
      </Container>
      <Container header="لیست دپارتمان ها">
        <div className="w-full">
          <Grid
            height={400}
            columns={columns}
            userData={departmentsData}
            pageSize={10}
            pagination={true}
            editBtn={true}
            deleteBtn={true}
            publishBtn={false}
            editable={false}
            btnHeader="عملیات"
            handleSetData={handleSetEditData}
            handleDelete={handleDelete}
          />
        </div>
      </Container>
    </div>
  );
}
